import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { countThisBrowser, setCountThisBrowser } from "./PageStats";

const PERIODS = [
  { days: 1, label: "Today" },
  { days: 7, label: "Last 7 days" },
  { days: 30, label: "Last 30 days" },
];

const n = (v) => (v || 0).toLocaleString();
const when = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");

function Tile({ label, value, note, testid }) {
  return (
    <div className="bg-zinc-900/60 border border-white/10 rounded-xl p-4" data-testid={testid}>
      <div className="text-xs font-mono-ui uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="font-display text-2xl font-bold mt-1">{n(value)}</div>
      {note && <div className="text-xs text-zinc-500 mt-1">{note}</div>}
    </div>
  );
}

// Admin: how many times each page was opened and each song was played, by guests, free members and Premium members.
// Dan's own visits (every admin, and the addresses on the "Leave these addresses out" list) are counted apart.
export default function AdminStats() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [counting, setCounting] = useState(countThisBrowser());

  useEffect(() => {
    let alive = true;
    setFailed(false);
    api.get("/admin/stats/visits", { params: { days } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [days]);

  const toggleCounting = () => {
    const next = !counting;
    setCountThisBrowser(next);
    setCounting(next);
    toast.success(next ? "Visits from this browser are counted again" : "Visits from this browser are no longer counted");
  };

  const empty = data && data.visits.total === 0 && data.plays.total === 0 && data.own.visits === 0 && data.own.plays === 0;

  return (
    <div data-testid="admin-visits">
      <h2 className="font-display text-2xl font-bold mb-4">Visits</h2>
      <div className="bg-zinc-900/60 border border-white/10 rounded-2xl p-4 sm:p-6">
        <p className="text-xs text-zinc-500">
          How many times each page was opened and each song was played. A visit is one time a page is opened. Your own visits and plays
          (every admin, and the addresses on your &ldquo;Leave these addresses out&rdquo; list under Email to members) are counted apart, so the numbers
          below are real people. Search engines and other robots are not counted. Only counts are kept: no names, no email addresses.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2" role="group" aria-label="Period">
          {PERIODS.map((p) => (
            <button key={p.days} type="button" onClick={() => setDays(p.days)} aria-pressed={days === p.days} data-testid={`admin-visits-days-${p.days}`}
              className={`px-3 py-1.5 rounded-full border text-xs ${days === p.days ? "border-cyan-400/60 bg-cyan-400/10 text-cyan-200" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>{p.label}</button>
          ))}
        </div>

        {failed && <div className="mt-4 text-sm text-zinc-400" data-testid="admin-visits-failed">The numbers could not be loaded. Refresh the page and try again.</div>}
        {!failed && !data && <div className="mt-4 text-sm text-zinc-500">Loading…</div>}
        {!failed && data && (
          <>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <Tile label="All visits" value={data.visits.total} testid="admin-visits-total" />
              <Tile label="Guests" value={data.visits.guest} testid="admin-visits-guest" />
              <Tile label="Free members" value={data.visits.member} testid="admin-visits-member" />
              <Tile label="Premium members" value={data.visits.premium} testid="admin-visits-premium" />
              <Tile label="New guests" value={data.new_guests} note="first time on this browser" testid="admin-visits-new" />
              <Tile label="Song plays" value={data.plays.total} note={`${n(data.plays.guest)} guests · ${n(data.plays.member)} free · ${n(data.plays.premium)} Premium`} testid="admin-visits-plays" />
            </div>
            <p className="mt-3 text-xs text-zinc-500" data-testid="admin-visits-own">
              Counted apart, not in the numbers above: your own {n(data.own.visits)} visits and {n(data.own.plays)} plays.
            </p>

            {empty ? (
              <div className="mt-4 text-sm text-zinc-500" data-testid="admin-visits-empty">Nothing counted yet for this period. Counting started the day this was installed.</div>
            ) : (
              <div className="mt-4 overflow-x-auto" data-testid="admin-visits-pages">
                <table className="w-full text-sm min-w-[34rem]">
                  <thead>
                    <tr className="text-left text-xs font-mono-ui uppercase tracking-widest text-zinc-500">
                      <th className="py-2 pr-3 font-normal">Page</th>
                      <th className="py-2 px-2 font-normal text-right">Guests</th>
                      <th className="py-2 px-2 font-normal text-right">Free members</th>
                      <th className="py-2 px-2 font-normal text-right">Premium</th>
                      <th className="py-2 px-2 font-normal text-right">All visits</th>
                      <th className="py-2 pl-2 font-normal text-right">Plays</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {data.pages.map((p) => (
                      <tr key={p.page} data-testid={`admin-visits-row-${p.page}`}>
                        <td className="py-2 pr-3 max-w-[18rem] truncate" title={p.label}>{p.label}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{n(p.guest)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{n(p.member)}</td>
                        <td className="py-2 px-2 text-right tabular-nums">{n(p.premium)}</td>
                        <td className="py-2 px-2 text-right tabular-nums font-semibold">{n(p.visits)}</td>
                        <td className="py-2 pl-2 text-right tabular-nums">{n(p.plays)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.pages_total > data.pages.length && <p className="pt-3 text-xs text-zinc-500">Showing the {data.pages.length} busiest pages, out of {data.pages_total}.</p>}
              </div>
            )}
          </>
        )}

        <label className="mt-5 flex items-start gap-3 text-sm cursor-pointer" data-testid="admin-visits-browser">
          <input type="checkbox" checked={!counting} onChange={toggleCounting} className="mt-1 h-4 w-4 accent-cyan-400" data-testid="admin-visits-nocount" />
          <span>
            Don&rsquo;t count visits from this browser
            <span className="block text-xs text-zinc-500">Turn this on in any browser where you look at your own site while logged out. You are logged in as an admin here, so those visits are already counted apart.</span>
          </span>
        </label>
      </div>
    </div>
  );
}

// Admin, Users list: the songs one member played, newest first.
export function UserPlays({ userId }) {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null); setFailed(false);
    api.get(`/admin/users/${userId}/plays`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setFailed(true); });
    return () => { alive = false; };
  }, [userId]);

  if (failed) return <div className="text-sm text-zinc-400" data-testid="admin-user-plays-failed">The list could not be loaded. Try again.</div>;
  if (!data) return <div className="text-sm text-zinc-500">Loading…</div>;
  if (data.items.length === 0) return <div className="text-sm text-zinc-500" data-testid="admin-user-plays-empty">Has not played any songs yet.</div>;
  return (
    <div data-testid="admin-user-plays-list">
      <div className="divide-y divide-white/10">
        {data.items.map((p) => (
          <div key={p.video_id} className="py-2 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm">
            <span className="min-w-0 truncate">{p.title}{p.artist ? <span className="text-zinc-500"> · {p.artist}</span> : null}</span>
            <span className="text-xs text-zinc-400">Last played {when(p.played_at)}{p.times ? ` · ${p.times} ${p.times === 1 ? "time" : "times"}` : ""}</span>
          </div>
        ))}
      </div>
      <p className="pt-2 text-xs text-zinc-500">
        {data.total > data.items.length ? `Showing the ${data.items.length} most recent songs, out of ${data.total}. ` : ""}
        One line per song. The number of times is counted from the day this was installed; older plays show only the last time.
      </p>
    </div>
  );
}